// Free Google Translate Service for BeeVids
// Uses unofficial but functional Google Translate endpoints

class GoogleTranslateService {
  constructor() {
    this.baseURL = 'https://translate.googleapis.com/translate_a/single';
    this.clientVersion = '2.1.0';
  }

  // Get supported languages (common ones for free service)
  getSupportedLanguages() {
    return [
      { code: 'ar', name: 'Arabic' },
      { code: 'zh', name: 'Chinese' },
      { code: 'nl', name: 'Dutch' },
      { code: 'en', name: 'English' },
      { code: 'fr', name: 'French' },
      { code: 'de', name: 'German' },
      { code: 'hi', name: 'Hindi' },
      { code: 'it', name: 'Italian' },
      { code: 'ja', name: 'Japanese' },
      { code: 'ko', name: 'Korean' },
      { code: 'pt', name: 'Portuguese' },
      { code: 'ru', name: 'Russian' },
      { code: 'es', name: 'Spanish' },
      { code: 'tr', name: 'Turkish' },
      { code: 'ur', name: 'Urdu' },
      { code: 'vi', name: 'Vietnamese' },
      { code: 'th', name: 'Thai' },
      { code: 'pl', name: 'Polish' },
      { code: 'sv', name: 'Swedish' },
      { code: 'da', name: 'Danish' },
      { code: 'no', name: 'Norwegian' },
      { code: 'fi', name: 'Finnish' },
      { code: 'he', name: 'Hebrew' },
      { code: 'fa', name: 'Persian' },
      { code: 'bg', name: 'Bulgarian' },
      { code: 'hr', name: 'Croatian' },
      { code: 'cs', name: 'Czech' },
      { code: 'et', name: 'Estonian' },
      { code: 'hu', name: 'Hungarian' },
      { code: 'lv', name: 'Latvian' },
      { code: 'lt', name: 'Lithuanian' },
      { code: 'ro', name: 'Romanian' },
      { code: 'sk', name: 'Slovak' },
      { code: 'sl', name: 'Slovenian' },
      { code: 'uk', name: 'Ukrainian' }
    ];
  }

  // Translate a single text using free Google Translate
  async translateText(text, targetLanguage, sourceLanguage = 'auto') {
    try {
      // Encode the text for URL
      const encodedText = encodeURIComponent(text);
      
      // Build the request URL for unofficial Google Translate API
      const url = `${this.baseURL}?client=gtx&sl=${sourceLanguage}&tl=${targetLanguage}&dt=t&q=${encodedText}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });

      if (!response.ok) {
        throw new Error(`Google Translate error: ${response.status}`);
      }

      const result = await response.json();
      
      // Parse the response - Google Translate returns an array structure
      if (result && result[0] && result[0][0] && result[0][0][0]) {
        const translatedText = result[0].map(item => item[0]).join('');
        const detectedLanguage = result[2] || sourceLanguage;
        
        return {
          success: true,
          translatedText: translatedText.trim(),
          detectedLanguage: detectedLanguage,
          confidence: 0.85 // Google doesn't provide confidence for free API
        };
      } else {
        throw new Error('Unexpected response format from Google Translate');
      }
    } catch (error) {
      console.error('Google Translate error:', error);
      return {
        success: false,
        error: error.message,
        translatedText: text // Return original text on failure
      };
    }
  }

  // Translate multiple segments with progress tracking
  async translateSegments(segments, targetLanguage, sourceLanguage = 'auto', onProgress) {
    const results = [];
    const batchSize = 3; // Smaller batches for free service to avoid rate limits
    
    for (let i = 0; i < segments.length; i += batchSize) {
      const batch = segments.slice(i, i + batchSize);
      const batchPromises = batch.map(async (segment) => {
        // Add small delay to respect rate limits
        await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
        
        const result = await this.translateText(segment.text, targetLanguage, sourceLanguage);
        
        return {
          ...segment,
          text: result.translatedText,
          originalText: segment.text,
          translationLanguage: targetLanguage,
          detectedLanguage: result.detectedLanguage,
          translationConfidence: result.confidence || 0.85,
          translationError: result.success ? null : result.error
        };
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      // Report progress
      const progress = Math.round((results.length / segments.length) * 100);
      if (onProgress) {
        onProgress(progress, `Translated ${results.length}/${segments.length} segments`);
      }

      // Longer delay between batches to be respectful
      if (i + batchSize < segments.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    return {
      success: true,
      translatedSegments: results,
      totalSegments: segments.length,
      targetLanguage,
      sourceLanguage
    };
  }

  // Test if the service is working
  async testConnection() {
    try {
      const result = await this.translateText('Hello world', 'es', 'en');
      return {
        success: result.success,
        message: result.success ? 'Google Translate (Free) is working!' : result.error
      };
    } catch (error) {
      return {
        success: false,
        message: `Google Translate test failed: ${error.message}`
      };
    }
  }

  // Get usage information (free service has no quota)
  async getUsageInfo() {
    return {
      success: true,
      service: 'Google Translate Free',
      quota: 'Unlimited',
      cost: 'Free',
      rateLimit: '~20 requests per minute (estimated)',
      note: 'This service is free but may have rate limits. Use respectfully.'
    };
  }
}

export default GoogleTranslateService; 